const FIELDS = {
  neck: (value) => value > 0,
  gauge_stitches: (value) => value > 0,
  gauge_width: (value) => value > 0,
  raglan_stitches: (value) => Number.isInteger(value) && value >= 0 && value <= 4,
  back_share: (value) => value >= 0 && value <= 100,
  front_share: (value) => value >= 0 && value <= 100,
};

const RAGLAN_FIELDS = ['neck', 'gauge_stitches', 'gauge_width', 'raglan_stitches'];

const MESSAGES = {
  invalid: 'Bitte gib eine gültige Nummer ein.',
  raglan_stitches: 'Bitte gib eine ganze Zahl von 0 bis 4 ein.',
  ratio: 'Rücken- und Vorderteil zusammen müssen unter 100 % liegen.',
  raglan: 'Zu viele Raglanmaschen für diesen Halsumfang.',
  send: 'Das hat leider nicht geklappt. Bitte versuche es noch einmal.',
};

const KLAVIYO = {
  url: 'https://a.klaviyo.com/client',
  companyId: 'RNjqkr',
  revision: '2026-07-15',
  metric: 'Raglan Calculation',
  listId: 'X2ty83',
  source: 'Raglan-Rechner',
};

const calculations = new WeakMap();

const parse = (raw) => {
  const value = raw.trim().replace(',', '.');
  return /^\d+(\.\d+)?$/.test(value) ? Number(value) : NaN;
};

const totalStitches = (values) => Math.round(values.neck * (values.gauge_stitches / values.gauge_width));

const calculate = (values) => {
  const total = totalStitches(values);
  const rest = total - 4 * values.raglan_stitches;
  const back = Math.round(rest * (values.back_share / 100));
  const front = Math.round(rest * (values.front_share / 100));
  const sleeve = Math.round((rest - back - front) / 2);
  return { total, back, front, sleeve, raglan: values.raglan_stitches };
};

const validate = (form) => {
  const values = {};
  const errors = {};

  Object.entries(FIELDS).forEach(([name, isValid]) => {
    values[name] = parse(form.elements[name].value);
    if (!isValid(values[name])) errors[name] = { message: MESSAGES[name] || MESSAGES.invalid, fields: [name] };
  });

  if (!errors.back_share && !errors.front_share && values.back_share + values.front_share >= 100) {
    errors.front_share = { message: MESSAGES.ratio, fields: ['back_share', 'front_share'] };
  }

  if (RAGLAN_FIELDS.every((name) => !errors[name]) && totalStitches(values) <= 4 * values.raglan_stitches) {
    errors.raglan_stitches = { message: MESSAGES.raglan, fields: RAGLAN_FIELDS };
  }

  return { values, errors };
};

const setError = (input, message) => {
  const error = input.closest('.ts-lg-raglan__control').querySelector('.ts-lg-raglan__error');
  const describedBy = (input.getAttribute('aria-describedby') || '').split(' ').filter((id) => id && id !== error.id);

  if (message) {
    error.textContent = message;
    describedBy.unshift(error.id);
    input.setAttribute('aria-invalid', 'true');
  } else {
    input.removeAttribute('aria-invalid');
  }

  error.hidden = !message;
  if (describedBy.length) {
    input.setAttribute('aria-describedby', describedBy.join(' '));
  } else {
    input.removeAttribute('aria-describedby');
  }
};

const update = (form) => {
  const { errors } = validate(form);

  Object.keys(FIELDS).forEach((name) => {
    const error = errors[name];
    const visible = error && error.fields.some((field) => form.elements[field].dataset.touched);
    setError(form.elements[name], visible ? error.message : null);
  });

  form.querySelector('.ts-lg-raglan__submit').disabled = Object.keys(errors).length > 0;
};

const show = (root, from, to) => {
  const header = document.querySelector('.header-section');
  const offset = header && ['sticky', 'fixed'].includes(getComputedStyle(header).position) ? header.offsetHeight : 0;

  from.hidden = true;
  to.hidden = false;
  to.focus({ preventScroll: true });
  window.scrollTo({
    top: root.getBoundingClientRect().top + window.scrollY - offset,
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
  });
};

const klaviyo = (endpoint, data) =>
  fetch(`${KLAVIYO.url}/${endpoint}?company_id=${KLAVIYO.companyId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO.revision },
    body: JSON.stringify({ data }),
  }).then((response) => {
    if (!response.ok) throw new Error(`Klaviyo ${endpoint}: ${response.status}`);
  });

const format = (value) => String(value).replace('.', ',');

const send = (root, form) => {
  const email = form.elements.email.value.trim();
  const { values, stitches } = calculations.get(root);
  const inputs = Object.fromEntries(Object.entries(values).map(([name, value]) => [name, format(value)]));

  const requests = [
    klaviyo('events', {
      type: 'event',
      attributes: {
        metric: { data: { type: 'metric', attributes: { name: KLAVIYO.metric } } },
        profile: { data: { type: 'profile', attributes: { email, properties: { market: root.dataset.market } } } },
        properties: { ...stitches, ...inputs },
      },
    }),
  ];

  if (form.elements.newsletter?.checked) {
    requests.push(
      klaviyo('subscriptions', {
        type: 'subscription',
        attributes: {
          custom_source: KLAVIYO.source,
          profile: {
            data: {
              type: 'profile',
              attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: KLAVIYO.listId } } },
      })
    );
  }

  return Promise.all(requests);
};

document.querySelectorAll('.ts-lg-raglan__form').forEach(update);

document.addEventListener('input', (event) => {
  const mirror = event.target.closest('.ts-lg-raglan__input-wrap')?.querySelector('.ts-lg-raglan__unit-mirror');
  if (mirror) mirror.textContent = event.target.value;

  const form = event.target.closest('.ts-lg-raglan__form');
  if (form) update(form);
});

document.addEventListener('focusout', (event) => {
  const form = event.target.closest('.ts-lg-raglan__form');
  if (!form || !Object.hasOwn(FIELDS, event.target.name)) return;
  event.target.dataset.touched = 'true';
  update(form);
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('.ts-lg-raglan__stepper-btn');
  if (!button) return;
  const input = button.parentElement.querySelector('.ts-lg-raglan__stepper-value');
  if (button.dataset.direction === 'up') {
    input.stepUp();
  } else {
    input.stepDown();
  }
  input.dataset.touched = 'true';
  input.dispatchEvent(new Event('input', { bubbles: true }));
});

document.addEventListener('submit', (event) => {
  const root = event.target.closest('.ts-lg-raglan');
  if (!root) return;
  event.preventDefault();

  if (event.target.matches('.ts-lg-raglan__email')) {
    const button = event.target.querySelector('.ts-lg-raglan__email-submit');
    const error = event.target.querySelector('.ts-lg-raglan__error');
    button.disabled = true;
    error.hidden = true;

    send(root, event.target)
      .then(() => show(root, root.querySelector('.ts-lg-raglan__result'), root.querySelector('.ts-lg-raglan__success')))
      .catch(() => {
        error.textContent = MESSAGES.send;
        error.hidden = false;
      })
      .finally(() => {
        button.disabled = false;
      });
    return;
  }

  const { values, errors } = validate(event.target);
  if (Object.keys(errors).length) return;

  const result = root.querySelector('.ts-lg-raglan__result');
  const stitches = calculate(values);
  calculations.set(root, { values, stitches });
  result.querySelectorAll('[data-result]').forEach((element) => {
    element.textContent = stitches[element.dataset.result];
  });
  show(root, event.target, result);
});
